<template>
  <div :class="['chat-message', `chat-message--${message.role}`]">
    <div class="chat-message__avatar">
      <v-avatar :color="message.role === AiAgentMessageRoleEnum.user ? 'primary' : 'secondary'" size="32">
        <v-icon size="small" color="white">
          {{ message.role === AiAgentMessageRoleEnum.user ? 'mdi-account' : 'mdi-robot' }}
        </v-icon>
      </v-avatar>
    </div>

    <div class="chat-message__content">
      <div class="chat-message__header">
        <span class="chat-message__sender">
          {{ message.role === AiAgentMessageRoleEnum.user ? 'You' : 'AI Assistant' }}
        </span>
        <span class="chat-message__time">
          {{ formatTimePassed(message.timestamp) }}
        </span>
      </div>

      <div class="chat-message__text" :class="{ 'chat-message__text--error': message.isError }">
        {{ message.content }}
      </div>

      <div v-if="hasRecordData" class="chat-message__records">
        <RecordCardRenderer
          :record-type="message.data.record_type"
          :items="message.data.items.result || []"
        />
      </div>

      <div v-if="message.executionTimeMs" class="chat-message__meta">
        <v-chip size="x-small" variant="outlined">
          <v-icon size="x-small" start>mdi-clock-outline</v-icon>
          {{ message.executionTimeMs }}ms
        </v-chip>
        <v-chip
          v-if="message.intent"
          size="x-small"
          variant="outlined"
          class="ml-2"
        >
          <v-icon size="x-small" start>mdi-tag-outline</v-icon>
          {{ message.intent }}
        </v-chip>
      </div>
    </div>
  </div>
</template>

<script lang="ts">
import { defineComponent } from 'vue';

import { AiAgentMessageRoleEnum, IAiMessage } from '@/bundles/AiAgent/types';
import { formatTimePassed } from '@/utils/filters';
import { isValuePresent } from '@/bundles/App/helpers/isValuePresent';

import RecordCardRenderer from '@/bundles/AiAgent/components/cards/RecordCardRenderer.vue';

interface IAiChatMessageInstance {
  AiAgentMessageRoleEnum?: typeof AiAgentMessageRoleEnum;
}

export default defineComponent({
  name: 'AiChatMessage',

  components: {
    RecordCardRenderer,
  },

  props: {
    message: {
      type: Object as () => IAiMessage,
      required: true,
    },
  },

  data: (): IAiChatMessageInstance => ({}),

  computed: {
    hasRecordData (): boolean {
      return isValuePresent(this.message.data?.items?.result);
    },
  },

  created: function () {
    this.AiAgentMessageRoleEnum = AiAgentMessageRoleEnum;
  },

  methods: {
    formatTimePassed,
  },
});
</script>

<style lang="scss" scoped>
.chat-message {
  display: flex;
  gap: 12px;
  padding: 12px 16px;

  &--user {
    flex-direction: row-reverse;

    .chat-message__content {
      align-items: flex-end;
    }

    .chat-message__text {
      background-color: #1976d2;
      color: white;
    }
  }

  // Theme grounds rather than fixed light greys, so the reply bubble is not a
  // bright patch on the dark page (#F5F5F5 in light, #3A3A3A in dark).
  &--assistant {
    .chat-message__text {
      background-color: rgb(var(--v-theme-sc-surface-soft));
      color: rgba(var(--v-theme-on-surface), var(--v-high-emphasis-opacity));

      &--error {
        background-color: rgba(var(--v-theme-error), 0.12);
      }
    }
  }

  &__avatar {
    flex-shrink: 0;
  }

  &__content {
    display: flex;
    flex-direction: column;
    gap: 4px;
    max-width: 70%;
    min-width: 0;
  }

  &__header {
    display: flex;
    align-items: center;
    gap: 8px;
    font-size: 12px;
  }

  &__sender {
    font-weight: 600;
    color: rgba(var(--v-theme-on-surface), var(--v-medium-emphasis-opacity));
  }

  &__time {
    color: rgba(var(--v-theme-on-surface), var(--v-disabled-opacity));
    font-size: 11px;
  }

  &__text {
    padding: 10px 14px;
    border-radius: 12px;
    word-wrap: break-word;
    line-height: 1.5;

    :deep(code) {
      background-color: rgba(var(--v-theme-on-surface), 0.1);
      padding: 2px 6px;
      border-radius: 4px;
      font-family: 'Courier New', monospace;
      font-size: 0.9em;
    }

    :deep(ul) {
      margin: 8px 0;
      padding-left: 20px;
    }

    :deep(li) {
      margin: 4px 0;
    }

    :deep(strong) {
      font-weight: 600;
    }

    :deep(a) {
      color: #1976d2;
      text-decoration: none;
      font-weight: 600;
      border-bottom: 1px solid #1976d2;

      &:hover {
        color: #1565c0;
        border-bottom-color: #1565c0;
      }
    }
  }

  // The brand blue reads 2.9:1 on the dark bubble; a lighter blue there.
  .v-theme--dark &--assistant &__text :deep(a) {
    color: #64B5F6;
    border-bottom-color: #64B5F6;

    &:hover {
      color: #90CAF9;
      border-bottom-color: #90CAF9;
    }
  }

  &__records {
    margin-top: 8px;
    width: 100%;
  }

  &__meta {
    display: flex;
    gap: 4px;
    margin-top: 4px;
  }
}
</style>
